import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import { useDropzone } from 'react-dropzone'
import { toast } from 'react-toastify'
import httpClient from 'Service'
import { APP } from 'Constants/App'

const CLOUD_UPLOAD_SVG =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242"/>' +
      '<path d="M12 12v9"/>' +
      '<path d="m16 16-4-4-4 4"/>' +
      '</svg>',
  )

type Props = {
  open: boolean
  onClose: () => void
  onInsert: (imageUrl: string) => void
}

type UploadApiEnvelope = {
  success?: boolean
  data?: { url?: string; path?: string } | string | null
  object?: string | { url?: string; path?: string } | null
}

function extractImagePathFromResponse(payload: unknown): string | null {
  if (payload == null) {
    return null
  }
  if (typeof payload === 'string') {
    return payload
  }
  if (typeof payload !== 'object') {
    return null
  }
  const top = payload as UploadApiEnvelope & Record<string, unknown>

  // POST /api/account/uploads/image — { success, message, data: { url, ... } }
  if (top.data != null && typeof top.data === 'object') {
    const d = top.data as { url?: string; path?: string }
    const fromNested = d.url ?? d.path
    if (typeof fromNested === 'string' && fromNested.length > 0) {
      return fromNested
    }
  }
  if (typeof top.data === 'string' && top.data.length > 0) {
    return top.data
  }

  // Legacy: { object: string | { url } }
  if (top.object != null) {
    const o = top.object
    if (typeof o === 'string') {
      return o
    }
    if (typeof o === 'object' && o) {
      const obj = o as { url?: string; path?: string }
      return obj.url ?? obj.path ?? null
    }
  }

  const flat = payload as Record<string, unknown>
  const v =
    flat.url ?? flat.path ?? flat.fileUrl ?? flat.imageUrl ?? flat.location ?? flat.href ?? flat.key
  return typeof v === 'string' ? v : null
}

function toPublicImageUrl(pathOrUrl: string): string {
  const t = pathOrUrl.trim()
  if (/^https?:\/\//i.test(t)) {
    return t
  }
  const base = (httpClient.defaults.baseURL || 'https://backend.tashkec.uz').replace(/\/$/, '')
  const path = t.startsWith('/') ? t : `/${t}`
  return `${base}${path}`
}

async function postAccountImage(file: File): Promise<string> {
  const formData = new FormData()
  formData.append('multipartFile ', file)

  const { data } = await httpClient.post<unknown>('/api/attachments/editor', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })

  const path = extractImagePathFromResponse(data)
  if (!path) {
    throw new Error('Unexpected upload response')
  }
  return toPublicImageUrl(path)
}

export function TextEditorImageUploadDialog({ open, onClose, onInsert }: Props) {
  const theme = useTheme()
  const [file, setFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [publicUrl, setPublicUrl] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const previewBlobRef = useRef<string | null>(null)

  const reset = useCallback(() => {
    if (previewBlobRef.current) {
      URL.revokeObjectURL(previewBlobRef.current)
      previewBlobRef.current = null
    }
    setFile(null)
    setPreviewUrl(null)
    setPublicUrl(null)
    setUploading(false)
  }, [])

  /** Clear current file / preview / URL so the user can choose another image (dialog stays open). */
  const clearCurrentUpload = useCallback(() => {
    if (previewBlobRef.current) {
      URL.revokeObjectURL(previewBlobRef.current)
      previewBlobRef.current = null
    }
    setFile(null)
    setPreviewUrl(null)
    setPublicUrl(null)
  }, [])

  useEffect(() => {
    if (!open) {
      reset()
    }
  }, [open, reset])

  const onDrop = useCallback((accepted: File[]) => {
    const next = accepted[0]
    if (!next) {
      return
    }
    if (previewBlobRef.current) {
      URL.revokeObjectURL(previewBlobRef.current)
      previewBlobRef.current = null
    }
    setFile(next)
    const local = URL.createObjectURL(next)
    previewBlobRef.current = local
    setPreviewUrl(local)
    setPublicUrl(null)

    void (async () => {
      setUploading(true)
      try {
        const url = await postAccountImage(next)
        setPublicUrl(url)
        if (previewBlobRef.current) {
          URL.revokeObjectURL(previewBlobRef.current)
          previewBlobRef.current = null
        }
        setPreviewUrl(url)
        toast.success('Изображение загружено')
      } catch {
        toast.error('Не удалось загрузить изображение')
        setPublicUrl(null)
      } finally {
        setUploading(false)
      }
    })()
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg', '.bmp'] },
    maxFiles: 1,
    disabled: uploading,
    multiple: false,
  })

  const panelBg = useMemo(
    () =>
      `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(
        theme.palette.secondary.main ?? theme.palette.primary.light,
        0.08,
      )} 100%)`,
    [theme.palette.primary.main, theme.palette.primary.light, theme.palette.secondary.main],
  )

  const handleCopy = useCallback(async () => {
    if (!publicUrl) {
      return
    }
    try {
      await navigator.clipboard.writeText(publicUrl)
      toast.success('Ссылка скопирована')
    } catch {
      toast.error('Не удалось скопировать')
    }
  }, [publicUrl])

  const handleInsert = useCallback(() => {
    if (!publicUrl) {
      return
    }
    onInsert(publicUrl)
    onClose()
    toast.success('Изображение добавлено в текст')
  }, [publicUrl, onInsert, onClose])

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm' scroll='body'>
      <Box
        sx={{
          px: 2.5,
          pt: 2.5,
          pb: 1,
          background: panelBg,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
        }}
      >
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <Box
            component='img'
            src={CLOUD_UPLOAD_SVG}
            alt=''
            sx={{ width: 28, height: 28, opacity: 0.9 }}
          />
          <Box>
            <Typography variant='h6' fontWeight={700} sx={{ lineHeight: 1.2 }}>
              Загрузка изображения
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Файл сохраняется на сервере — в редактор попадает ссылка, без тяжёлого base64
            </Typography>
          </Box>
        </Stack>
      </Box>

      <DialogContent sx={{ pt: 3, pb: 1 }}>
        <Stack spacing={2.5}>
          <Box
            {...getRootProps()}
            sx={{
              borderRadius: 2,
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : alpha(theme.palette.primary.main, 0.35),
              backgroundColor: isDragActive ? alpha(theme.palette.primary.main, 0.06) : alpha(theme.palette.grey[100], 0.5),
              px: 2,
              py: 4,
              textAlign: 'center',
              cursor: uploading ? 'wait' : 'pointer',
              transition: 'border-color 0.2s, background-color 0.2s',
              '&:hover': {
                borderColor: uploading ? undefined : 'primary.main',
                backgroundColor: uploading ? undefined : alpha(theme.palette.primary.main, 0.04),
              },
            }}
          >
            <input {...getInputProps()} />
            {uploading ? (
              <Stack alignItems='center' spacing={1}>
                <CircularProgress size={36} thickness={4} />
                <Typography variant='body2' color='text.secondary'>
                  Загрузка…
                </Typography>
              </Stack>
            ) : (
              <>
                <Typography variant='subtitle2' fontWeight={600} gutterBottom>
                  Перетащите файл сюда или нажмите для выбора
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  PNG, JPG, WebP, GIF и другие изображения
                </Typography>
              </>
            )}
          </Box>

          {previewUrl && !uploading && (
            <Stack spacing={1}>
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                  boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.06)}`,
                  backgroundColor: theme.palette.grey[50],
                }}
              >
                <Box
                  component='img'
                  src={previewUrl}
                  alt={file?.name ?? 'preview'}
                  sx={{
                    display: 'block',
                    width: '100%',
                    maxHeight: 240,
                    objectFit: 'contain',
                  }}
                />
              </Box>
              <Button
                size='small'
                variant='outlined'
                color='inherit'
                onClick={clearCurrentUpload}
                sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600 }}
              >
                Сбросить и выбрать другое
              </Button>
            </Stack>
          )}

          <TextField
            fullWidth
            size='small'
            label='Публичный URL изображения'
            value={publicUrl ?? ''}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position='end' sx={{ ml: 0 }}>
                  <Button
                    size='small'
                    variant='outlined'
                    onClick={handleCopy}
                    disabled={!publicUrl}
                    sx={{ flexShrink: 0, textTransform: 'none', fontWeight: 600 }}
                  >
                    Скопировать
                  </Button>
                </InputAdornment>
              ),
            }}
            placeholder='После загрузки здесь появится адрес'
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1 }}>
        <Button className='light' onClick={onClose} disabled={uploading}>
          {APP.CANCEL}
        </Button>
        <Button variant='contained' onClick={handleInsert} disabled={!publicUrl || uploading}>
          Вставить в текст
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export const TEXT_EDITOR_CLOUD_IMAGE_ICON = CLOUD_UPLOAD_SVG
